// payment.ts
import Stripe from 'stripe';

interface PlanDetails {
  id: string;
  name: string;
  price: number;
  currency: string;
  features: string[];
  interval: 'month' | 'year';
}

// Define subscription plans
export const SUBSCRIPTION_PLANS: Record<string, PlanDetails> = {
  beta: {
    id: 'price_1QcDHqGvWKeICZYYTwtTo76Y',
    name: 'Beta',
    price: 10,
    currency: 'USD',
    interval: 'month',
    features: [
      '30 messages per hour',
      'Longer message context',
      'Priority support'
    ]
  },
  alpha: {
    id: 'price_1QcDJCGvWKeICZYYlOgXOqZ2',
    name: 'Alpha',
    price: 25,
    currency: 'USD',
    interval: 'month',
    features: [
      '100 messages per hour',
      'Maximum context length',
      'Early access to new features',
      'VIP support'
    ]
  }
};

export interface PaymentProcessor {
  createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string>;
  handleWebhook(
    payload: any,
    signature: string,
    env: Env
  ): Promise<void>;
}

// Stripe Payment Processor
export class StripePaymentProcessor implements PaymentProcessor {
  private stripe: Stripe;
  private webhookSecret: string;

  constructor(apiKey: string, webhookSecret: string) {
    this.stripe = new Stripe(apiKey, {
      apiVersion: '2023-10-16'
    });
    this.webhookSecret = webhookSecret;
  }

  async createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    const session = await this.stripe.checkout.sessions.create({
      customer_email: userId,
      line_items: [{
        price: planId,
        quantity: 1
      }],
      mode: 'subscription',
      success_url: successUrl,
      cancel_url: cancelUrl
    });

    return session.url || '';
  }

  async handleWebhook(payload: any, signature: string, env: Env): Promise<void> {
    try {
      const event = this.stripe.webhooks.constructEvent(
        payload,
        signature,
        this.webhookSecret
      );

      switch (event.type) {
        case 'checkout.session.completed':
          await this.handleCheckoutCompleted(event.data.object, env);
          break;
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdated(event.data.object, env);
          break;
        case 'customer.subscription.deleted':
          await this.handleSubscriptionCanceled(event.data.object, env);
          break;
      }
    } catch (err) {
      console.error('Webhook Error:', err);
      throw err;
    }
  }

  private async handleCheckoutCompleted(session: any, env: Env): Promise<void> {
    const userId = session.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    // Update user tier based on the subscription
    const subscription = await this.stripe.subscriptions.retrieve(session.subscription);
    const tier = this.getTierFromPrice(subscription.items.data[0].price.id);
    
    user.tier = tier;
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private async handleSubscriptionUpdated(subscription: any, env: Env): Promise<void> {
    const userId = subscription.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    const tier = this.getTierFromPrice(subscription.items.data[0].price.id);
    user.tier = tier;
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private async handleSubscriptionCanceled(subscription: any, env: Env): Promise<void> {
    const userId = subscription.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    user.tier = 'free';
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private getTierFromPrice(priceId: string): 'free' | 'beta' | 'alpha' {
    switch (priceId) {
      case SUBSCRIPTION_PLANS.beta.id:
        return 'beta';
      case SUBSCRIPTION_PLANS.alpha.id:
        return 'alpha';
      default:
        return 'free';
    }
  }
}

// Bitcoin Cash Payment Processor
export class BCHPaymentProcessor implements PaymentProcessor {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    // Implement BCH payment flow
    // This would typically involve:
    // 1. Generating a unique BCH address for the payment
    // 2. Creating an order in your system
    // 3. Returning a URL to your BCH payment page
    
    const plan = SUBSCRIPTION_PLANS[planId];
    if (!plan) {
      throw new Error('Invalid plan');
    }

    // Here you would integrate with your BCH payment provider
    // This is a placeholder for the actual implementation
    return `/bch-payment?orderId=${orderId}&amount=${plan.price}&userId=${userId}`;
  }

  async handleWebhook(payload: any, signature: string, env: Env): Promise<void> {
    // Implement BCH webhook handling
    // This would typically involve:
    // 1. Verifying the payment was received
    // 2. Updating the user's subscription status
    // 3. Handling subscription renewals
  }
}
